import { useEffect, useMemo, useState } from 'react'
import { Banknote, CreditCard, LoaderCircle, UserRound } from 'lucide-react'
import { formatLbp, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Modal, Toggle } from '@/components/ui/primitives'
import type { AttachedCustomer } from '@/stores/cart'
import { t } from '@/i18n'

export interface Tender {
  method: 'CashUsd' | 'CashLbp' | 'Card' | 'OnAccount' | 'Transfer'
  amountUsd: number
  amountLbp: number
  reference: string | null
}

/** Notes a Lebanese till actually holds, for the quick-tender buttons. */
const USD_NOTES = [1, 5, 10, 20, 50, 100]
const LBP_NOTES = [50_000, 100_000, 500_000, 1_000_000]

export function PaymentSheet({
  open,
  onClose,
  totalUsd,
  rate,
  roundingStep,
  customer,
  isSubmitting,
  error,
  onConfirm,
  onAttachCustomer,
}: {
  open: boolean
  onClose: () => void
  totalUsd: number
  rate: number
  roundingStep: number
  customer: AttachedCustomer | null
  isSubmitting: boolean
  error: string | null
  onConfirm: (tenders: Tender[], changeInLbp: boolean) => void
  onAttachCustomer: () => void
}) {
  const [cashUsd, setCashUsd] = useState('')
  const [cashLbp, setCashLbp] = useState('')
  const [card, setCard] = useState('')
  const [onAccount, setOnAccount] = useState('')
  const [changeInLbp, setChangeInLbp] = useState(false)

  useEffect(() => {
    if (open) {
      setCashUsd('')
      setCashLbp('')
      setCard('')
      setOnAccount('')
      setChangeInLbp(false)
    }
  }, [open])

  const tendered = useMemo(() => {
    const lbpAsUsd = Number(cashLbp || 0) / rate
    return Number(cashUsd || 0) + lbpAsUsd + Number(card || 0) + Number(onAccount || 0)
  }, [cashUsd, cashLbp, card, onAccount, rate])

  const remaining = totalUsd - tendered
  const change = remaining < 0 ? -remaining : 0

  // Mirrors the server exactly. Lira is snapped to note-sized steps, so a lira tender can only
  // ever get within half a step of what is owed — at 5,000 against a rate near 89,000 that is
  // nearly three cents. Holding the button to a flat cent refused a customer who had handed
  // over the only amount real notes can make.
  const liraTolerance = Math.max(0.01, roundingStep / 2 / rate)
  const settleWithin = Number(cashLbp || 0) > 0 ? liraTolerance : 0.01
  const isSettled = remaining <= settleWithin

  // What is still owed once the real money is counted. An account charge may cover this and no
  // more: charging beyond it would turn the surplus into change, which is the shop handing out
  // cash against credit it has not been paid. The server refuses it either way; this stops the
  // cashier finding out only after the customer has walked to the door.
  const paidByMoney =
    Number(cashUsd || 0) + Number(cashLbp || 0) / rate + Number(card || 0)
  const chargeable = Math.max(0, totalUsd - paidByMoney)
  const availableCredit = customer ? Math.max(0, customer.availableCreditUsd) : 0
  const overCredit = Number(onAccount || 0) - availableCredit > 0.01
  const overChargeable = Number(onAccount || 0) - chargeable > 0.01

  /**
   * Lira, snapped to a note-sized step.
   *
   * No combination of notes pays an arbitrary amount, so anything a customer is asked for or
   * handed back is rounded to the step the shop set. Applied to each figure the cashier acts on
   * rather than once to the total, because what is asked for after fifty dollars has already been
   * counted is a different amount from the whole bill.
   */
  const lira = (usd: number) => Math.round((usd * rate) / roundingStep) * roundingStep

  const totalLbpRounded = lira(totalUsd)
  const changeLbpRounded = lira(change)

  // What is left to collect. Split payment is the normal case here — 52 dollars settled as fifty
  // in notes and the rest in lira — and the cashier needs the lira figure for the remainder, not
  // for the whole bill, or they ask for the wrong amount.
  const outstanding = Math.max(0, remaining)
  const outstandingLbp = lira(outstanding)

  function confirm() {
    const tenders: Tender[] = []
    if (Number(cashUsd) > 0) {
      tenders.push({ method: 'CashUsd', amountUsd: Number(cashUsd), amountLbp: 0, reference: null })
    }
    if (Number(cashLbp) > 0) {
      tenders.push({ method: 'CashLbp', amountUsd: 0, amountLbp: Number(cashLbp), reference: null })
    }
    if (Number(card) > 0) {
      tenders.push({ method: 'Card', amountUsd: Number(card), amountLbp: 0, reference: null })
    }
    if (Number(onAccount) > 0) {
      tenders.push({ method: 'OnAccount', amountUsd: Number(onAccount), amountLbp: 0, reference: null })
    }
    onConfirm(tenders, changeInLbp)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Take payment')}
      description={`${formatUsd(totalUsd)} · ${formatLbp(totalLbpRounded)}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('Cancel')}
          </Button>
          <Button
            size="lg"
            onClick={confirm}
            disabled={!isSettled || isSubmitting || overCredit || overChargeable}
          >
            {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {isSubmitting ? t('Completing…') : t('Complete sale')}
          </Button>
        </>
      }
    >
      <div className="surface-sunken mb-4 rounded-xl p-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-[var(--text-secondary)]">{t('Due')}</span>
          <span className="tabular text-2xl font-semibold">{formatUsd(totalUsd)}</span>
        </div>
        <div className="tabular mt-1 text-right text-sm text-[var(--text-secondary)]">
          {formatLbp(totalLbpRounded)} at {rate.toLocaleString()}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div>
          <Input
            label={t('Cash — US dollars')}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={cashUsd}
            onChange={(e) => setCashUsd(e.target.value)}
            leading={<Banknote className="h-4 w-4" />}
            className="tabular text-lg"
            placeholder="0.00"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {/* What is still owed, not the whole bill: after 100,000 lira has gone in, "exact"
                means the dollars that finish the sale. */}
            <Button
              variant="secondary"
              size="sm"
              disabled={outstanding <= 0}
              onClick={() =>
                setCashUsd((Number(cashUsd || 0) + outstanding).toFixed(2))
              }
            >
              {t('Exact')}
            </Button>
            {/* Every note that could settle what is left, not the smallest few. Capping the
                list dropped the 50 and the 100 off the end of a small sale — so the one case
                where a customer hands over a big note was the case with no button for it. */}
            {USD_NOTES.filter((note) => note >= outstanding).map((note) => (
              <Button
                key={note}
                variant="secondary"
                size="sm"
                onClick={() => setCashUsd(String(note))}
                className="tabular"
              >
                ${note}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <Input
            label={t('Cash — Lebanese lira')}
            type="number"
            inputMode="numeric"
            step={roundingStep}
            min="0"
            value={cashLbp}
            onChange={(e) => setCashLbp(e.target.value)}
            leading={<Banknote className="h-4 w-4" />}
            className="tabular text-lg"
            placeholder="0"
            hint={cashLbp ? `= ${formatUsd(Number(cashLbp) / rate)}` : undefined}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={outstanding <= 0}
              onClick={() => setCashLbp(String(Number(cashLbp || 0) + outstandingLbp))}
              className="tabular"
            >
              {t('Exact')}
            </Button>
            {LBP_NOTES.filter((note) => note >= outstandingLbp).map((note) => (
              <Button
                key={note}
                variant="secondary"
                size="sm"
                onClick={() => setCashLbp(String(note))}
                className="tabular"
              >
                {(note / 1000).toLocaleString()}k
              </Button>
            ))}
          </div>
        </div>

        <Input
          label={t('Card')}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={card}
          onChange={(e) => setCard(e.target.value)}
          leading={<CreditCard className="h-4 w-4" />}
          className="tabular"
          placeholder="0.00"
        />

        {customer ? (
          <div>
            <Input
              label={`On account — ${customer.name}`}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={onAccount}
              onChange={(e) => setOnAccount(e.target.value)}
              leading={<UserRound className="h-4 w-4" />}
              className="tabular"
              placeholder="0.00"
              hint={
                availableCredit > 0
                  ? `${formatUsd(availableCredit)} of credit left · owes ${formatUsd(customer.balanceUsd)}`
                  : `No credit left — owes ${formatUsd(customer.balanceUsd)} of a ${formatUsd(customer.creditLimitUsd)} limit`
              }
            />

            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={chargeable <= 0}
                onClick={() => setOnAccount(chargeable.toFixed(2))}
              >
                {t('Charge the rest')}
              </Button>
              {onAccount !== '' && (
                <Button variant="ghost" size="sm" onClick={() => setOnAccount('')}>
                  {t('Clear')}
                </Button>
              )}
            </div>

            {/* Said plainly at the till rather than left to the server's rejection, because the
                cashier has to tell the customer something before the sale can be completed. */}
            {overCredit && (
              <p role="alert" className="mt-2 text-sm text-danger-500">
                {t('That is past the credit limit for {name} — {amount} is all that can go on the account.', { name: customer.name, amount: formatUsd(availableCredit) })}
              </p>
            )}
            {!overCredit && overChargeable && (
              <p role="alert" className="mt-2 text-sm text-danger-500">
                Only {formatUsd(chargeable)} is left to put on account; the rest is already paid.
              </p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onAttachCustomer}
            className="flex items-center gap-2 self-start text-sm text-[var(--text-secondary)] underline-offset-4 hover:underline"
          >
            <UserRound className="h-4 w-4" />
            {t('Put this on a customer\'s account')}
          </button>
        )}
      </div>

      <div className="surface-sunken mt-5 rounded-xl p-4">
        {remaining > settleWithin ? (
          <div>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-warn-500">{t('Still to pay')}</span>
              <span className="tabular text-xl font-semibold text-warn-500">
                {formatUsd(remaining)}
              </span>
            </div>
            {/* The same shortfall in lira. A customer paying the balance in notes has to be told
                a lira figure, and the cashier converting it in their head at the till is how the
                wrong amount gets asked for. */}
            <div className="tabular mt-1 text-right text-sm text-warn-500">
              or {formatLbp(outstandingLbp)}
            </div>
          </div>
        ) : Number(onAccount || 0) > 0 ? (
          // Checked before change, not after: an account charge that exceeds what is owed would
          // otherwise read as change the till is about to hand over, which is precisely the thing
          // that must not happen. "Settled" would be a lie too — no money has come in for this
          // part, and the drawer will not hold it at close.
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium">
              On {customer?.name ?? 'account'}
              {paidByMoney > 0.01 && `, ${formatUsd(paidByMoney)} paid now`}
            </span>
            <span className="tabular text-xl font-semibold text-warn-500">
              {formatUsd(Number(onAccount))}
            </span>
          </div>
        ) : change > 0 ? (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium">{t('Change')}</span>
              <span className="tabular text-xl font-semibold text-success-500">
                {changeInLbp ? formatLbp(changeLbpRounded) : formatUsd(change)}
              </span>
            </div>
            {/* Both figures, always. Which one is handed over is the toggle below; seeing the
                other is how a cashier decides, and how they check the drawer has it. */}
            <div className="tabular mt-1 text-right text-sm text-[var(--text-secondary)]">
              {changeInLbp ? `= ${formatUsd(change)}` : `or ${formatLbp(changeLbpRounded)}`}
            </div>
            <div className="mt-2">
              <Toggle
                checked={changeInLbp}
                onChange={setChangeInLbp}
                label={t('Give change in lira')}
                hint={`Rounded to the nearest ${roundingStep.toLocaleString()} LL`}
              />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t('Settled exactly')}</span>
            <Badge tone="success">{t('No change')}</Badge>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          {error}
        </p>
      )}
    </Modal>
  )
}
